'use client';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { Badge } from '@m/components-ui/badge';
import { Button } from '@m/components-ui/button';
import {
  DateFilterPill,
  type DateFilterPillProps,
} from '@m/components-ui/date-and-time/date-filter-pill';
import { SectionHeader } from '@m/components-ui/mobile-rows';
import { KeyboardArrowRight, ParkingPass } from '@m/icons';
import Link from 'next/link';

import { DateBadge } from './date-badge';

export { DateBadge } from './date-badge';

export type EventListFilter = DateFilterPillProps;

export interface EventRowItem {
  /** ISO date string or Date object for the event */
  date: Date | string;
  /**
   * Destination URL for the row. When set, the row and "Find parking" button
   * render as Next `Link`s. Use this from server components where function
   * props (`onFindParking`) cannot cross the boundary.
   */
  href?: string;
  /** Callback when the "Find parking" button is clicked */
  onFindParking?: () => void;
  /** Parking price label, e.g. "Parking starting at $13" */
  parkingLabel?: string;
  /** Display label for when the event occurs, e.g. "Thursday • 02:00PM" */
  timeLabel: string;
  /** Full event name */
  title: string;
}

export interface EventRowGroupProps {
  items: EventRowItem[];
  /** Month/year label for the group, e.g. "February 2026" */
  monthLabel: string;
}

export interface EventListProps {
  /** Optional subtitle below the heading */
  description?: string;
  /** Optional date filter; renders a pill that opens a date picker popover */
  filter?: EventListFilter;
  /** Grouped event rows, each representing a calendar month */
  groups: EventRowGroupProps[];
  /** Whether more events are currently being loaded; shows a spinner on the view-more button */
  isLoadingMore?: boolean;
  /** Optional "View more events" footer button callback */
  onViewMore?: () => void;
  /** Custom label for the view-more button */
  viewMoreLabel?: string;
}

const TITLE = 'Upcoming events';
const EMPTY_TITLE = 'No upcoming events found';
const EMPTY_DESCRIPTION =
  'We couldn’t find any upcoming events. Please try adjusting your date filter.';

/**
 * A single event list item row.
 * Responsive: on md+ shows a "Find parking" button; on base shows a chevron.
 *
 * When `item.href` is set, the entire row is wrapped in a Next `Link` (via
 * `<Box asChild>`) so the row works in server components. When only
 * `item.onFindParking` is set, the row becomes a button via `role="button"`.
 * The inner button and chevron set `pointerEvents="none"` so a single click
 * always bubbles to the row's wrapper — no `stopPropagation` plumbing.
 */
const EventRow = ({ item }: { item: EventRowItem }) => {
  const { href, onFindParking: action } = item;
  const isClickable = !!href || !!action;

  const content = (
    <HStack py={3} gap={4} width="100%" align="center" justify="space-between">
      <HStack gap={{ base: 4, md: 6 }} flex={1} minW={0} align="center">
        <DateBadge date={item.date} />
        <Stack gap={2} flex={1} minW={0} align="flex-start">
          <Stack gap={1} width="100%">
            <Text
              variant="MainBodyMedium"
              color="text.primary"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {item.title}
            </Text>
            <Text variant="SmallBodyRegular" color="text.secondary">
              {item.timeLabel}
            </Text>
          </Stack>
          {item.parkingLabel && (
            <Badge variant="brand" icon={<ParkingPass />}>
              {item.parkingLabel}
            </Badge>
          )}
        </Stack>
      </HStack>
      {isClickable && (
        <>
          <Button
            variant="primary"
            size="sm"
            flexShrink={0}
            display={{ base: 'none', md: 'inline-flex' }}
            pointerEvents="none"
            tabIndex={-1}
          >
            Find parking
          </Button>
          <Box
            as="span"
            display={{ base: 'inline-flex', md: 'none' }}
            boxSize="24px"
            fontSize="24px"
            lineHeight="1"
            flexShrink={0}
            color="fill.icon.interactive"
            pointerEvents="none"
            aria-hidden
          >
            <KeyboardArrowRight />
          </Box>
        </>
      )}
    </HStack>
  );

  const rowProps = {
    bg: 'fill.background.default',
    width: '100%',
    display: 'block',
    color: 'inherit',
    textDecoration: 'none',
    borderBottom: '1px solid',
    borderColor: 'stroke.divider.default',
    cursor: isClickable ? 'pointer' : 'default',
    _hover: { textDecoration: 'none' },
  } as const;

  const buttonProps =
    action && !href
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: action,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              action();
            }
          },
        }
      : undefined;

  return (
    <Box asChild={!!href} {...rowProps} {...buttonProps}>
      {href ? <Link href={href}>{content}</Link> : content}
    </Box>
  );
};

/** Calendar-with-X illustration shown in the empty state */
const NoEventsIllustration = () => (
  <Box width="63px" height="65px" flexShrink={0}>
    <svg
      width="63"
      height="65"
      viewBox="0 0 63 65"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3.92721 12.6343C3.80226 7.82104 7.67003 3.85156 12.4849 3.85156H48.769C53.4969 3.85156 57.3296 7.68427 57.3296 12.4121V53.0749C57.3296 57.8028 53.4969 61.6355 48.769 61.6355H13.5405C8.89911 61.6355 5.10321 57.9368 4.98276 53.2971L3.92721 12.6343Z"
        fill="#5F59FF"
      />
      <path
        d="M13.3398 4.06543H50.0693C54.5115 4.06555 58.1751 7.54518 58.4043 11.9814L60.5029 52.6445C60.7493 57.4169 56.9466 61.4219 52.168 61.4219H15.7646C11.3479 61.4219 7.69549 57.9802 7.43262 53.5713L5.00879 12.9092C4.72281 8.11277 8.53495 4.06558 13.3398 4.06543Z"
        fill="white"
        stroke="black"
        strokeWidth="0.428029"
      />
      <path
        d="M19.2344 0.427734V6.42014"
        stroke="black"
        strokeWidth="0.856058"
        strokeLinecap="round"
      />
      <path
        d="M46.2012 0.427734V6.42014"
        stroke="black"
        strokeWidth="0.856058"
        strokeLinecap="round"
      />
      <path
        d="M26.0781 32.293L43.4704 49.1958"
        stroke="#5F59FF"
        strokeWidth="1.63327"
        strokeLinecap="round"
      />
      <path
        d="M26.3242 49.4395L43.2271 32.0472"
        stroke="#5F59FF"
        strokeWidth="1.63327"
        strokeLinecap="round"
      />
      <path
        d="M10.1992 51.1699V53.3101C10.1992 56.6196 12.8821 59.3025 16.1916 59.3025H17.9037"
        stroke="black"
        strokeWidth="0.428029"
        strokeLinecap="round"
      />
      <path
        d="M5.21411 12.7193C4.97554 8.07017 8.68078 4.16992 13.336 4.16992H50.0789C54.3653 4.16992 57.9158 7.49682 58.1943 11.7741L58.8711 22.1699H5.69908L5.21411 12.7193Z"
        fill="#E0DFFF"
      />
      <path
        d="M5.80078 22.1699H58.8008"
        stroke="black"
        strokeWidth="0.428029"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

/** Empty state shown when there are no events to display */
const EmptyState = ({ title, description }: { description: string; title: string }) => (
  <Stack gap={4} align="center" justify="center" py={4} px={6} width="100%">
    <NoEventsIllustration />
    <Stack gap={2} align="center" textAlign="center" maxWidth="333px">
      <Text variant="MainBodyDemiBold" color="text.primary">
        {title}
      </Text>
      <Text variant="SmallBodyRegular" color="text.secondary">
        {description}
      </Text>
    </Stack>
  </Stack>
);

/** A month group with a section header and its event rows */
const EventRowGroup = ({ group }: { group: EventRowGroupProps }) => (
  <Stack gap={0} width="100%">
    <SectionHeader title={group.monthLabel} padding={3} titleVariant="Subheader" />
    {group.items.map((item, idx) => (
      <EventRow key={`${item.date}-${idx}`} item={item} />
    ))}
  </Stack>
);

/**
 * EventList renders an upcoming events section with a heading, optional filter,
 * and grouped event rows organized by calendar month. Responsive: on base, the
 * filter pill stacks below the heading; on md+, it sits inline.
 */
export const EventList = ({
  description,
  groups,
  filter,
  onViewMore,
  viewMoreLabel = 'View more events',
  isLoadingMore = false,
}: EventListProps) => {
  const isEmpty = groups.every((group) => group.items.length === 0);

  return (
    <Stack gap={0} pt={8} width="100%">
      <Stack
        direction={{ base: 'column', md: 'row' }}
        py={4}
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        gap={4}
      >
        <SectionHeader title={TITLE} padding={0}>
          {description}
        </SectionHeader>
        {filter && <DateFilterPill {...filter} />}
      </Stack>
      {isEmpty ? (
        <EmptyState title={EMPTY_TITLE} description={EMPTY_DESCRIPTION} />
      ) : (
        <Stack gap={0}>
          {groups.map((group) => (
            <EventRowGroup key={group.monthLabel} group={group} />
          ))}
        </Stack>
      )}
      {onViewMore && !isEmpty && (
        <HStack justify="center" pt={4} pb={8}>
          <Button variant="tertiary" size="md" onClick={onViewMore} loading={isLoadingMore}>
            {viewMoreLabel}
          </Button>
        </HStack>
      )}
    </Stack>
  );
};
